import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import InlineClockPicker from './InlineClockPicker';
import {
  apply12HourParts,
  clampDateTime,
  get12HourParts,
} from './timePickerUtils';
import './materialDateTimePicker.css';

const findScrollParent = (element) => {
  let parent = element?.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    const overflow = style.overflow;
    if (/(auto|scroll|overlay)/.test(overflowY) || /(auto|scroll|overlay)/.test(overflow)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

const MaterialDateTimePicker = ({
  selected,
  onChange,
  customInput,
  minDate,
  maxDate,
  minTime,
  maxTime,
  isClearable = false,
  shouldCloseOnScroll = false,
}) => {
  const wrapperRef = useRef(null);
  const popperRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('date');
  const [clockMode, setClockMode] = useState('hour');
  const [draftDate, setDraftDate] = useState(selected || null);
  const [placement, setPlacement] = useState('below');
  const [isReady, setIsReady] = useState(false);

  const getAnchorElement = useCallback(() => {
    if (!wrapperRef.current) return null;
    return (
      wrapperRef.current.querySelector('.customDateTrigger')
      || wrapperRef.current.querySelector('.mdt-trigger-fallback')
      || wrapperRef.current
    );
  }, []);

  const updatePlacement = useCallback(() => {
    const anchor = getAnchorElement();
    const popper = popperRef.current;
    if (!anchor || !popper) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popperHeight = popper.offsetHeight || 420;
    const scrollParent = findScrollParent(wrapperRef.current);
    const boundaryBottom = scrollParent
      ? scrollParent.getBoundingClientRect().bottom
      : window.innerHeight;
    const boundaryTop = scrollParent
      ? scrollParent.getBoundingClientRect().top
      : 0;

    const spaceBelow = boundaryBottom - anchorRect.bottom;
    const spaceAbove = anchorRect.top - boundaryTop;

    if (spaceBelow >= popperHeight + 8) {
      setPlacement('below');
    } else if (spaceAbove >= popperHeight + 8) {
      setPlacement('above');
    } else {
      setPlacement(spaceBelow >= spaceAbove ? 'below' : 'above');
    }
  }, [getAnchorElement]);

  const isPickerInternalScroll = useCallback((target) => {
    if (!target || !(target instanceof Node)) return false;
    return Boolean(popperRef.current?.contains(target));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setDraftDate(selected || null);
      setIsReady(false);
    }
  }, [selected, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const openFrame = requestAnimationFrame(() => {
      updatePlacement();
      requestAnimationFrame(() => setIsReady(true));
    });
    return () => cancelAnimationFrame(openFrame);
  }, [isOpen, updatePlacement]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleScroll = (event) => {
      if (isPickerInternalScroll(event.target)) return;
      if (shouldCloseOnScroll) {
        setIsOpen(false);
        return;
      }
      updatePlacement();
    };

    const handleResize = () => updatePlacement();

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, isPickerInternalScroll, shouldCloseOnScroll, updatePlacement]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutside = (event) => {
      if (
        wrapperRef.current?.contains(event.target) ||
        popperRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [isOpen]);

  const commitDate = useCallback(
    (nextDate) => {
      if (!nextDate || !moment(nextDate).isValid()) {
        setDraftDate(null);
        onChange?.(null);
        return;
      }

      const normalized = moment(nextDate).second(0).millisecond(0).toDate();
      const clamped = clampDateTime(normalized, minDate, maxDate, minTime, maxTime);
      setDraftDate(clamped);
      onChange?.(clamped);
    },
    [maxDate, maxTime, minDate, minTime, onChange]
  );

  const openPicker = () => {
    const base = selected && moment(selected).isValid() ? selected : new Date();
    setDraftDate(base);
    setActiveTab('date');
    setClockMode('hour');
    setPlacement('below');
    setIsReady(false);
    setIsOpen(true);
  };

  const handleDateChange = (date) => {
    if (!date) return;
    const current = draftDate && moment(draftDate).isValid() ? moment(draftDate) : moment();
    const merged = moment(date)
      .hour(current.hour())
      .minute(current.minute())
      .second(0)
      .millisecond(0)
      .toDate();
    commitDate(merged);
  };

  const handleHourSelect = (date) => {
    commitDate(date);
    setClockMode('minute');
  };

  const handleMinuteSelect = (date) => {
    commitDate(date);
  };

  const handleAmPmToggle = (isPM) => {
    if (!draftDate || !moment(draftDate).isValid()) return;
    const { hour12, minutes } = get12HourParts(draftDate);
    commitDate(apply12HourParts(draftDate, hour12, minutes, isPM));
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDraftDate(null);
    onChange?.(null);
    setIsOpen(false);
  };

  const handleOk = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (draftDate && moment(draftDate).isValid()) {
      commitDate(draftDate);
    }
    setIsOpen(false);
  };

  const switchToTimeTab = () => {
    setActiveTab('time');
    setClockMode('hour');
  };
  const switchToDateTab = () => setActiveTab('date');

  const currentDate = draftDate && moment(draftDate).isValid() ? draftDate : new Date();
  const { hour12, minutes, isPM } = get12HourParts(currentDate);
  const displayTime = `${hour12}:${minutes.toString().padStart(2, '0')}`;

  const renderMonthHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div className="mdt-calendar__nav">
      <button
        type="button"
        className="mdt-calendar__nav-btn"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        aria-label="Previous month"
      >
        ‹
      </button>
      <span className="mdt-calendar__month">{moment(date).format('MMMM YYYY')}</span>
      <button
        type="button"
        className="mdt-calendar__nav-btn"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        aria-label="Next month"
      >
        ›
      </button>
    </div>
  );

  const trigger = customInput
    ? React.cloneElement(customInput, {
        value: selected,
        onClick: openPicker,
      })
    : (
      <button type="button" className="mdt-trigger-fallback" onClick={openPicker}>
        {selected && moment(selected).isValid()
          ? moment(selected).format('D MMM YY, h:mm A')
          : 'Select date & time'}
      </button>
    );

  return (
    <div className="calender material-datetime-picker" ref={wrapperRef}>
      {trigger}
      {isOpen && (
        <div
          ref={popperRef}
          className={`mdt-picker-popper mdt-picker-popper--${placement}${isReady ? ' is-ready' : ''}`}
          role="dialog"
          aria-label="Date and time picker"
        >
          <div className="mdt-picker">
            <div className="mdt-picker__header">
              <div className="mdt-picker__year">{moment(currentDate).format('YYYY')}</div>
              <div className="mdt-picker__summary">
                <button
                  type="button"
                  className={`mdt-picker__date-btn${activeTab === 'date' ? ' is-active' : ''}`}
                  onClick={switchToDateTab}
                >
                  {moment(currentDate).format('MMM D')}
                </button>
                <div className="mdt-picker__time-wrap">
                  <button
                    type="button"
                    className={`mdt-picker__time-btn${activeTab === 'time' ? ' is-active' : ''}`}
                    onClick={switchToTimeTab}
                  >
                    {displayTime}
                  </button>
                  <div className="mdt-picker__ampm">
                    <button
                      type="button"
                      className={`mdt-picker__ampm-btn${!isPM ? ' is-active' : ''}`}
                      onClick={() => handleAmPmToggle(false)}
                    >
                      AM
                    </button>
                    <button
                      type="button"
                      className={`mdt-picker__ampm-btn${isPM ? ' is-active' : ''}`}
                      onClick={() => handleAmPmToggle(true)}
                    >
                      PM
                    </button>
                  </div>
                </div>
              </div>
              <div className="mdt-picker__tabs">
                <button
                  type="button"
                  className={`mdt-picker__tab${activeTab === 'date' ? ' is-active' : ''}`}
                  onClick={switchToDateTab}
                  aria-label="Date"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`mdt-picker__tab${activeTab === 'time' ? ' is-active' : ''}`}
                  onClick={switchToTimeTab}
                  aria-label="Time"
                >
                  <svg
                    className="mdt-picker__tab-icon mdt-picker__tab-icon--clock"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="9" fill="none" />
                    <path d="M12 7v5l3.5 2" fill="none" />
                  </svg>
                </button>
                <span
                  className="mdt-picker__tab-indicator"
                  style={{ transform: activeTab === 'date' ? 'translateX(0)' : 'translateX(100%)' }}
                />
              </div>
            </div>

            <div className="mdt-picker__body">
              <div className={`mdt-picker__panel${activeTab === 'date' ? ' is-active' : ''}`}>
                <DatePicker
                  inline
                  selected={currentDate}
                  onChange={handleDateChange}
                  minDate={minDate}
                  maxDate={maxDate}
                  renderCustomHeader={renderMonthHeader}
                  calendarClassName="mdt-calendar"
                />
              </div>
              <div className={`mdt-picker__panel mdt-picker__panel--time${activeTab === 'time' ? ' is-active' : ''}`}>
                <InlineClockPicker
                  value={currentDate}
                  mode={clockMode}
                  onHourSelect={handleHourSelect}
                  onMinuteSelect={handleMinuteSelect}
                  onBackToHour={() => setClockMode('hour')}
                />
              </div>
            </div>

            <div className="mdt-picker__footer">
              {isClearable && selected && moment(selected).isValid() ? (
                <button type="button" className="mdt-picker__clear" onClick={handleClear}>
                  Clear
                </button>
              ) : (
                <span className="mdt-picker__footer-spacer" />
              )}
              <button type="button" className="mdt-picker__ok" onClick={handleOk}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialDateTimePicker;
